import type { CreateTenantAdmin, DirectoryUser, TenantAdminRole } from '@wf-coe/types';
import {
  Badge,
  Button,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from 'flowbite-react';
import { type FormEvent, useEffect, useState } from 'react';
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useCreateTenantAdminMutation } from '../../queries/use-create-tenant-admin.js';
import { useDeleteTenantAdminMutation } from '../../queries/use-delete-tenant-admin.js';
import { useDirectoryUsersQuery } from '../../queries/use-directory-users.js';
import { type TenantAdminDto, useTenantAdminsQuery } from '../../queries/use-tenant-admins.js';
import { useUpdateTenantAdminMutation } from '../../queries/use-update-tenant-admin.js';

const ROLE_OPTIONS: TenantAdminRole[] = ['Admin', 'Operator', 'Viewer'];

const ROLE_BADGE_COLOR: Record<TenantAdminRole, string> = {
  Admin: 'failure',
  Operator: 'info',
  Viewer: 'gray',
};

/**
 * `/clients/{id}/admins` — manages per-tenant operator assignments
 * (ADR 0015). Adding an admin today asks for the operator's Entra OID
 * + email + display name as text input; the next slice adds a Microsoft
 * Graph user-search picker so the operator can be chosen by name.
 */
export function ClientAdmins(): JSX.Element {
  const { id: clientId } = useParams<{ id: string }>();
  const { data, error, isLoading, isError } = useTenantAdminsQuery(clientId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<TenantAdminDto | null>(null);
  const [deleting, setDeleting] = useState<TenantAdminDto | null>(null);

  const handleNew = (): void => {
    setEditing(null);
    setEditorOpen(true);
  };
  const handleEdit = (admin: TenantAdminDto): void => {
    setEditing(admin);
    setEditorOpen(true);
  };

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Tenant admins</h2>
        <Button size="sm" onClick={handleNew} disabled={!clientId}>
          <FiPlus className="mr-1" /> Add admin
        </Button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
          Loading tenant admins…
        </p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Failed to load tenant admins'}
        </div>
      ) : data && data.length === 0 ? (
        <EmptyState onCreate={handleNew} />
      ) : data ? (
        <AdminsTable admins={data} onEdit={handleEdit} onDelete={setDeleting} />
      ) : null}

      {clientId ? (
        <AdminEditModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          clientId={clientId}
          existing={editing}
        />
      ) : null}
      {clientId && deleting ? (
        <AdminDeleteModal
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          clientId={clientId}
          admin={deleting}
        />
      ) : null}
    </section>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-600 dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">No admins yet</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
        Assign operators to this tenant. Effective capability is the minimum of their global Entra
        role and the role granted here (per ADR 0015).
      </p>
      <Button size="sm" className="mx-auto mt-4" onClick={onCreate}>
        <FiPlus className="mr-1" /> Add admin
      </Button>
    </div>
  );
}

function AdminsTable({
  admins,
  onEdit,
  onDelete,
}: {
  admins: TenantAdminDto[];
  onEdit: (admin: TenantAdminDto) => void;
  onDelete: (admin: TenantAdminDto) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>Name / email</TableHeadCell>
            <TableHeadCell>Role</TableHeadCell>
            <TableHeadCell>Granted</TableHeadCell>
            <TableHeadCell>
              <span className="sr-only">Actions</span>
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
          {admins.map((admin) => (
            <TableRow key={admin.userOid} className="bg-white dark:bg-gray-800">
              <TableCell>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {admin.displayName ?? admin.email}
                </div>
                {admin.displayName ? (
                  <div className="text-xs text-gray-500 dark:text-gray-400">{admin.email}</div>
                ) : null}
              </TableCell>
              <TableCell>
                <Badge color={ROLE_BADGE_COLOR[admin.role]}>{admin.role}</Badge>
              </TableCell>
              <TableCell className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(admin.grantedAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit(admin)}
                    aria-label={`Edit role for ${admin.email}`}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(admin)}
                    aria-label={`Remove ${admin.email}`}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** 300ms debounce for the directory-search query — avoids fanning out a Graph call per keystroke. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function AdminEditModal({
  open,
  onClose,
  clientId,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  existing: TenantAdminDto | null;
}): JSX.Element {
  const create = useCreateTenantAdminMutation();
  const update = useUpdateTenantAdminMutation();
  const [userOid, setUserOid] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<TenantAdminRole>('Operator');
  const [pickerQuery, setPickerQuery] = useState('');
  const [picked, setPicked] = useState<DirectoryUser | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const debouncedQuery = useDebouncedValue(pickerQuery, 300);
  const directoryQuery = useDirectoryUsersQuery(manualMode ? undefined : debouncedQuery);

  useEffect(() => {
    if (open) {
      setUserOid(existing?.userOid ?? '');
      setEmail(existing?.email ?? '');
      setDisplayName(existing?.displayName ?? '');
      setRole(existing?.role ?? 'Operator');
      setPickerQuery('');
      setPicked(null);
      // When editing an existing assignment, the identity fields are
      // immutable + already populated — no point re-picking. Skip the
      // search UI entirely in that case.
      setManualMode(existing !== null);
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.userOid]);

  const isPending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const isEditing = existing !== null;
  const canSubmit = isEditing
    ? !isPending
    : userOid.trim() !== '' && email.trim() !== '' && !isPending;

  const handlePick = (user: DirectoryUser): void => {
    setPicked(user);
    setUserOid(user.id);
    setEmail(user.mail);
    setDisplayName(user.displayName);
  };

  const handleClearPick = (): void => {
    setPicked(null);
    setUserOid('');
    setEmail('');
    setDisplayName('');
    setPickerQuery('');
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (isEditing) {
      update.mutate(
        { clientId, userOid: existing.userOid, patch: { role } },
        { onSuccess: onClose },
      );
    } else {
      const body: CreateTenantAdmin = {
        userOid: userOid.trim(),
        email: email.trim(),
        displayName: displayName.trim() === '' ? null : displayName.trim(),
        role,
      };
      create.mutate({ clientId, body }, { onSuccess: onClose });
    }
  };

  // 503 from the API → directory search isn't configured for this env.
  // Auto-fall-back to manual mode so the operator isn't stuck.
  const directoryUnavailable =
    !manualMode &&
    directoryQuery.isError &&
    directoryQuery.error instanceof Error &&
    /(503|502)/.test(directoryQuery.error.message);

  return (
    <Modal show={open} onClose={onClose} dismissible size="lg">
      <ModalHeader>{isEditing ? 'Change role' : 'Add tenant admin'}</ModalHeader>
      <form onSubmit={handleSubmit} aria-label="Tenant admin form">
        <ModalBody>
          <div className="space-y-4">
            {!isEditing && !manualMode ? (
              <DirectorySearchSection
                query={pickerQuery}
                onQuery={setPickerQuery}
                results={directoryQuery.data ?? []}
                isLoading={directoryQuery.isFetching}
                isError={directoryQuery.isError && !directoryUnavailable}
                error={directoryQuery.error}
                picked={picked}
                onPick={handlePick}
                onClearPick={handleClearPick}
                onSwitchToManual={() => setManualMode(true)}
                disabled={isPending}
              />
            ) : null}

            {directoryUnavailable ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                Directory search isn't configured in this environment.{' '}
                <button type="button" className="underline" onClick={() => setManualMode(true)}>
                  Switch to manual entry
                </button>
                .
              </div>
            ) : null}

            {(manualMode || isEditing) && !picked ? (
              <ManualFields
                userOid={userOid}
                setUserOid={setUserOid}
                email={email}
                setEmail={setEmail}
                displayName={displayName}
                setDisplayName={setDisplayName}
                disabled={isPending || isEditing}
                showPickerLink={!isEditing}
                onSwitchToPicker={() => {
                  setManualMode(false);
                  handleClearPick();
                }}
              />
            ) : null}

            <div>
              <Label htmlFor="ta-role">
                Role <span className="text-red-500">*</span>
              </Label>
              <Select
                id="ta-role"
                value={role}
                onChange={(e) => setRole(e.target.value as TenantAdminRole)}
                disabled={isPending}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
              <HelperText className="mt-1">
                Effective capability is min(global role, this role).
              </HelperText>
            </div>
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error instanceof Error ? error.message : 'Save failed'}
              </div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="gray" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? 'Saving…' : isEditing ? 'Save' : 'Grant access'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function DirectorySearchSection({
  query,
  onQuery,
  results,
  isLoading,
  isError,
  error,
  picked,
  onPick,
  onClearPick,
  onSwitchToManual,
  disabled,
}: {
  query: string;
  onQuery: (q: string) => void;
  results: DirectoryUser[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  picked: DirectoryUser | null;
  onPick: (user: DirectoryUser) => void;
  onClearPick: () => void;
  onSwitchToManual: () => void;
  disabled: boolean;
}): JSX.Element {
  if (picked) {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">{picked.displayName}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">{picked.mail}</div>
            <div className="mt-1 font-mono text-[10px] text-gray-500">{picked.id}</div>
          </div>
          <button
            type="button"
            className="text-xs text-blue-700 underline dark:text-blue-300"
            onClick={onClearPick}
            disabled={disabled}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor="ta-search">
        Search Entra directory <span className="text-red-500">*</span>
      </Label>
      <div className="relative">
        <FiSearch
          className="pointer-events-none absolute top-3 left-3 text-gray-400"
          size={16}
          aria-hidden
        />
        <TextInput
          id="ta-search"
          type="search"
          autoComplete="off"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Name or email…"
          disabled={disabled}
          className="[&_input]:pl-9"
        />
      </div>
      {query.trim().length < 2 ? (
        <HelperText className="mt-1">Type at least 2 characters to search.</HelperText>
      ) : null}
      {isError ? (
        <div
          role="alert"
          className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Search failed'}
        </div>
      ) : null}
      {isLoading ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          <Spinner size="sm" />
          Searching…
        </div>
      ) : null}
      {!isLoading && results.length > 0 ? (
        <ul className="mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => onPick(u)}
                disabled={disabled}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {u.displayName}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{u.mail}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {!isLoading && !isError && query.trim().length >= 2 && results.length === 0 ? (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">No users matched.</div>
      ) : null}
      <button
        type="button"
        onClick={onSwitchToManual}
        className="mt-2 text-xs text-gray-600 underline dark:text-gray-400"
      >
        Or enter the OID manually
      </button>
    </div>
  );
}

function ManualFields({
  userOid,
  setUserOid,
  email,
  setEmail,
  displayName,
  setDisplayName,
  disabled,
  showPickerLink,
  onSwitchToPicker,
}: {
  userOid: string;
  setUserOid: (s: string) => void;
  email: string;
  setEmail: (s: string) => void;
  displayName: string;
  setDisplayName: (s: string) => void;
  disabled: boolean;
  showPickerLink: boolean;
  onSwitchToPicker: () => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="ta-oid">
          Entra user oid <span className="text-red-500">*</span>
        </Label>
        <TextInput
          id="ta-oid"
          type="text"
          required
          value={userOid}
          onChange={(e) => setUserOid(e.target.value)}
          disabled={disabled}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        />
      </div>
      <div>
        <Label htmlFor="ta-email">
          Email <span className="text-red-500">*</span>
        </Label>
        <TextInput
          id="ta-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div>
        <Label htmlFor="ta-display-name">Display name</Label>
        <TextInput
          id="ta-display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={disabled}
        />
      </div>
      {showPickerLink ? (
        <button
          type="button"
          onClick={onSwitchToPicker}
          className="text-xs text-gray-600 underline dark:text-gray-400"
        >
          Or search the directory instead
        </button>
      ) : null}
    </div>
  );
}

function AdminDeleteModal({
  open,
  onClose,
  clientId,
  admin,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  admin: TenantAdminDto;
}): JSX.Element {
  const del = useDeleteTenantAdminMutation();

  const handleConfirm = (): void => {
    del.mutate({ clientId, userOid: admin.userOid }, { onSuccess: onClose });
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="md">
      <ModalHeader>Remove tenant admin</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This removes <span className="font-semibold">{admin.displayName ?? admin.email}</span>{' '}
          from this tenant. They keep their global Entra role; this only revokes access to{' '}
          <em>this</em> tenant's resources once enforcement is on.
        </p>
        {del.isError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {del.error instanceof Error ? del.error.message : 'Failed to remove'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button color="failure" onClick={handleConfirm} disabled={del.isPending}>
          {del.isPending ? 'Removing…' : 'Remove'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
